import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Heart, MapPin, MessageCircle, CalendarCheck, Star, ShieldCheck, Lock, CreditCard, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg, eur, dateFr } from "@/lib/api";
import { PageShell, Photo, Pill, StatusPill, PriceBreakdown } from "@/components/common";
import { useMe } from "@/context/MeContext";
import { ReportDialog } from "@/components/ReportDialog";
import { OfferPicker } from "@/components/OfferPicker";

function Gallery({ photos, title }) {
  const [i, setI] = useState(0);
  return (
    <div className="space-y-3">
      <Photo src={photos?.[i]} alt={title} className="w-full aspect-[4/5] sm:aspect-[4/3] rounded-2xl" />
      {photos?.length > 1 && (
        <div className="flex gap-2">
          {photos.map((p, idx) => (
            <button key={p} data-testid={`gallery-thumb-${idx}`} onClick={() => setI(idx)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === idx ? "border-forest" : "border-transparent"}`}>
              <Photo src={p} alt="" className="w-full h-full" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnerPanel({ l, reload }) {
  const api = useApi();
  const [appeal, setAppeal] = useState("");
  const close = async () => {
    try { await api.post(`/listings/${l.id}/close`); toast.success("Annonce clôturée"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sendAppeal = async () => {
    try { await api.post(`/listings/${l.id}/appeal`, { text: appeal }); toast.success("Recours envoyé"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl bg-muted p-6 space-y-4" data-testid="owner-panel">
      <p className="font-semibold text-forest">C'est votre annonce</p>
      <div className="flex gap-2 flex-wrap"><StatusPill status={l.status} testid="owner-listing-status" /><StatusPill status={l.moderation_status} testid="owner-moderation-status" /></div>
      {l.flags?.length > 0 && <p className="text-xs text-amber-800">Contrôle automatique : {l.flags.join(", ")} — en attente de modération.</p>}
      <p className="text-sm flex items-center gap-2" data-testid="owner-exact-address"><Lock className="w-4 h-4" />Adresse privée : {l.exact_address || "non renseignée"}</p>
      {l.moderation_reason && <p className="text-xs text-stone-600">Motif : {l.moderation_reason}</p>}
      <div className="flex gap-2 flex-wrap">
        {l.status === "active" && <Link to={`/publier/${l.id}`} data-testid="owner-edit-btn"><Button variant="outline" className="rounded-full"><Pencil className="w-4 h-4 mr-1" />Modifier</Button></Link>}
        {l.status === "active" && <Button data-testid="owner-close-btn" variant="outline" className="rounded-full" onClick={close}>Clôturer</Button>}
        <Link to="/compte?tab=reservations"><Button data-testid="owner-reservations-btn" className="rounded-full bg-forest hover:bg-forest/90">Gérer les réservations</Button></Link>
      </div>
      {l.moderation_status === "rejected" && (
        <div className="space-y-2">
          <Textarea data-testid="appeal-input" value={appeal} onChange={(e) => setAppeal(e.target.value)} placeholder="Expliquez pourquoi l'annonce est conforme" />
          <Button data-testid="appeal-submit-btn" onClick={sendAppeal} variant="outline" className="rounded-full">Faire un recours</Button>
        </div>
      )}
    </div>
  );
}

function BuyerPanel({ l, reload }) {
  const api = useApi();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const guard = (fn) => (...args) => (isSignedIn ? fn(...args) : navigate("/sign-in"));
  const contact = guard(async () => {
    try { const { data } = await api.post("/conversations", { listing_id: l.id }); navigate(`/messages?c=${data.id}`); } catch (e) { toast.error(errMsg(e)); }
  });
  const reserve = guard(async () => {
    try { await api.post(`/listings/${l.id}/reservations`, { message: msg }); toast.success("Demande de réservation envoyée"); reload(); } catch (e) { toast.error(errMsg(e)); }
  });
  const res = l.my_reservation;
  const makeOffer = guard(async (amount) => {
    try {
      const { data } = await api.post("/conversations", { listing_id: l.id });
      await api.post(`/conversations/${data.id}/messages`, { text: "", offer_amount: amount });
      toast.success(`Offre de ${eur(amount)} envoyée au vendeur`);
      navigate(`/messages?c=${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  });
  return (
    <div className="space-y-4" data-testid="buyer-panel">
      {res?.status === "accepted" && (
        <div className="rounded-2xl bg-forest text-sand p-6 space-y-3" data-testid="reservation-accepted-box">
          <p className="font-semibold flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Réservation acceptée</p>
          <p className="text-sm opacity-90">Expire le {dateFr(res.expires_at)}</p>
          <p className="text-sm flex items-center gap-2" data-testid="exact-address"><Lock className="w-4 h-4" /> Adresse de remise : {l.exact_address}</p>
          {l.type === "sale" ? (
            <Link to={`/paiement/${l.id}`}><Button data-testid="go-to-payment-btn" className="rounded-full bg-terra hover:bg-terra/90 w-full mt-2"><CreditCard className="w-4 h-4 mr-2" />Payer et choisir la remise</Button></Link>
          ) : (
            <Link to={`/paiement/${l.id}`}><Button data-testid="donation-delivery-btn" className="rounded-full bg-white text-forest hover:bg-sand w-full mt-2"><CreditCard className="w-4 h-4 mr-2" />Régler les frais de don (remise ou livraison)</Button></Link>
          )}
        </div>
      )}
      {res?.status === "pending" && <p data-testid="reservation-pending-msg" className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm">Demande de réservation envoyée — en attente de {l.type === "donation" ? "validation par le donneur" : "réponse du vendeur"}.</p>}
      {!res && l.status === "active" && (
        <>
          <Textarea data-testid="reservation-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={l.type === "donation" ? "Présentez-vous au donneur (sans contrepartie financière)" : "Un mot pour le vendeur (facultatif)"} />
          <Button data-testid="reserve-btn" onClick={reserve} className="w-full rounded-full bg-forest hover:bg-forest/90 h-12"><CalendarCheck className="w-4 h-4 mr-2" />{l.type === "donation" ? "Demander ce don" : "Demander la réservation"}</Button>
        </>
      )}
      {l.status !== "active" && !res && <p className="text-sm text-stone-500" data-testid="listing-unavailable">Cette annonce n'est plus disponible.</p>}
      <Button data-testid="contact-seller-btn" onClick={contact} variant="outline" className="w-full rounded-full h-12"><MessageCircle className="w-4 h-4 mr-2" />Contacter</Button>
      {l.type === "sale" && l.status === "active" && !res && (
        <div className="rounded-2xl bg-muted p-4" data-testid="listing-offer-box">
          <p className="font-semibold text-forest mb-2">Faire une offre</p>
          <OfferPicker price={l.price} onSend={makeOffer} />
        </div>
      )}
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const api = useApi();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [error, setError] = useState(null);
  const { me, toggleFav: toggleCtxFav } = useMe();

  const load = useCallback(() => {
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch((e) => setError(errMsg(e)));
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const toggleFav = async () => {
    if (!isSignedIn) return navigate("/sign-in");
    const on = await toggleCtxFav(id);
    setL({ ...l, is_favorite: on });
  };

  if (error) return <PageShell><p data-testid="listing-error">{error}</p></PageShell>;
  if (!l) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  const isOwner = me?.user_id === l.owner_id;

  return (
    <PageShell>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7"><Gallery photos={l.photos} title={l.title} /></div>
        <div className="lg:col-span-5 space-y-6">
          <div className="flex items-center gap-2 flex-wrap">
            {l.type === "donation" ? <Pill tone="terra">Don</Pill> : <Pill tone="forest">Vente</Pill>}
            <Pill>{l.category}</Pill><Pill tone="sand">{l.condition}</Pill>
            {l.status !== "active" && <StatusPill status={l.status} />}
            {l.demo && <Pill tone="warn">Annonce de démonstration</Pill>}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-forest tracking-tight leading-none" data-testid="listing-title">{l.title}</h1>
          <div className="flex items-center justify-between">
            <p className="font-serif text-4xl text-stone-900" data-testid="listing-price">{l.type === "donation" ? "Gratuit" : eur(l.price)}<span className="block font-sans text-sm text-stone-500">{l.type === "sale" ? "prix demandé par le vendeur" : "don"}</span></p>
            <button data-testid="favorite-btn" onClick={toggleFav} aria-label="Favori" className="rounded-full border border-stone-200 bg-white p-3 hover:border-terra transition-colors">
              <Heart className={`w-5 h-5 ${l.is_favorite ? "fill-terra text-terra" : "text-stone-500"}`} />
            </button>
          </div>
          <p className="flex items-center gap-2 text-sm text-stone-600" data-testid="listing-location"><MapPin className="w-4 h-4" /> {l.city} ({l.postal_code}) — lieu approximatif</p>
          <PriceBreakdown l={l} />
          {isOwner ? <OwnerPanel l={l} reload={load} /> : <BuyerPanel l={l} reload={load} />}
          <Link to={`/profil/${l.owner_id}`} data-testid="seller-profile-link" className="flex items-center gap-4 rounded-2xl bg-white border border-stone-200 p-4 hover:border-forest transition-colors">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center font-serif text-xl text-forest">{l.owner.display_name[0]}</div>
            <div className="flex-1">
              <p className="font-medium">{l.owner.company_name || l.owner.display_name}</p>
              <p className="text-xs text-stone-500 flex items-center gap-2">
                {l.owner.rating ? <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{l.owner.rating} ({l.owner.reviews_count})</span> : "Pas encore d'avis"}
                {l.owner.verified && <span className="flex items-center gap-1 text-forest"><ShieldCheck className="w-3 h-3" />Vérifié</span>}
                {l.owner.role === "pro" && <Pill>Pro</Pill>}
              </p>
            </div>
          </Link>
          <div>
            <p className="eyebrow mb-2">Description</p>
            <p className="text-stone-700 whitespace-pre-line" data-testid="listing-description">{l.description}</p>
          </div>
          <ReportDialog targetType="listing" targetId={l.id} label="Signaler cette annonce" testid="report-listing-btn" />
        </div>
      </div>
    </PageShell>
  );
}
